import { useEffect, useState } from "react";
import { Fingerprint, ScanFace, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  isBiometricAvailable, enableBiometric, disableBiometric, hasStoredBiometric, getBiometryLabel,
} from "@/lib/biometric";
import { getAccessToken } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

/**
 * Profile → Security toggle for Biometric Unlock (Face ID / Touch ID).
 * Shown only on native iOS/Android devices; on web it renders a disabled explainer.
 */
export default function BiometricToggleCard() {
  const { user } = useAuth();
  const [available, setAvailable] = useState(null); // null = loading
  const [label, setLabel] = useState("Biometrics");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { available: canUse } = await isBiometricAvailable();
      setAvailable(canUse);
      setLabel(await getBiometryLabel());
      if (canUse) setEnabled(await hasStoredBiometric());
    })();
  }, []);

  const onToggle = async (next) => {
    if (busy) return;
    setBusy(true);
    if (next) {
      const token = getAccessToken();
      if (!token || !user?.email) {
        toast.error("Please sign in again before enabling biometric unlock.");
        setBusy(false); return;
      }
      const result = await enableBiometric(token, user.email);
      setBusy(false);
      if (result.ok) {
        setEnabled(true);
        toast.success(`${label} unlock enabled.`);
      } else {
        toast.error(`Couldn't enable ${label}: ${result.reason || "try again"}`);
      }
    } else {
      await disableBiometric();
      setBusy(false);
      setEnabled(false);
      toast.success(`${label} unlock disabled.`);
    }
  };

  if (available === null) return null;
  const Icon = label === "Face ID" ? ScanFace : Fingerprint;

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="biometric-toggle-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm flex items-center gap-1.5">
            {label} Unlock
            {enabled && <ShieldCheck className="w-3.5 h-3.5 text-green-600" />}
          </h3>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5">
            {available
              ? `Skip the password on app open. Your session is stored in the iOS Keychain, unlocked only by your ${label.toLowerCase()}.`
              : `${label} unlock is only available in the Elevated Psychiatry iOS/Android app. Install from the App Store to enable it.`}
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={onToggle}
          disabled={!available || busy}
          aria-label={`Toggle ${label} unlock`}
          data-testid="biometric-toggle-switch"
        />
      </div>
    </div>
  );
}
